import { useToast } from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { fileUploadInstance } from '@/modules/fileUpload'
import { GroupMembersPersonaInfoSummary } from '@/modules/group/modules/groups/domain/models/GroupMembers'
import { PersonaGroup } from '@/modules/group/modules/groups/domain/models/PersonaGroup'
import { FileExtension } from '@/modules/shared/schemas/FileExtension'
import Api from '@/shared/services/api'
import { CreateFileRequestDTO } from '@/shared/services/api/gallery/upload-files'
import { isFileOverLimit } from '@/ui/utils/checkFileSize'
import { resizeHDImage } from '@/ui/utils/resizeImg'

type CreateFileRequestWithURL = CreateFileRequestDTO & { fileUrl: string }

const galleryContext = createContext<{
  isLoading: boolean
  isGroup: boolean
  setIsGroup: (isGroup: boolean) => void
  groupId: string
  setGroupId: (groupId: string) => void
  groupInfo: PersonaGroup | undefined
  setGroupInfo: (personaGroup: PersonaGroup | undefined) => void
  membersGroup: GroupMembersPersonaInfoSummary[]
  setMembersGroup: (membersGroup: GroupMembersPersonaInfoSummary[]) => void
  filesUpload: File[]
  setFilesUpload: (files: File[]) => void
}>({
  groupId: '',
  setGroupId: () => {},
  groupInfo: undefined,
  setGroupInfo: () => {},
  membersGroup: [],
  setMembersGroup: () => {},
  filesUpload: [],
  setFilesUpload: () => {},
  isGroup: false,
  setIsGroup: () => {},
  isLoading: false
})

export const useGalleryContext = () => useContext(galleryContext)

export default function GalleryProvider({
  children
}: {
  children: React.ReactNode
}) {
  const queryClient = useQueryClient()
  const [groupId, setGroupId] = useState(
    () => localStorage.getItem('current-group-id') ?? ''
  )
  const { id: folderId } = useParams()
  const [groupInfo, setGroupInfo] = useState<PersonaGroup | undefined>(
    undefined
  )
  const [membersGroup, setMembersGroup] = useState<
    GroupMembersPersonaInfoSummary[]
  >([])
  const [isGroup, setIsGroup] = useState(false)

  const [filesUpload, setFilesUpload] = useState<File[]>([])
  const toast = useToast()
  const groupInfoQuery = useQuery({
    queryKey: ['info', groupId, isGroup],
    queryFn: async () => {
      const [response, error] = await Api.getGroupInfo(groupId)

      if (error) {
        throw error
      }
      return response.data
    },
    enabled: !!groupId && isGroup
  })

  useEffect(() => {
    if (groupInfoQuery.data) {
      setGroupInfo(groupInfoQuery.data)
    }
  }, [groupInfoQuery.data])

  useEffect(() => {
    if (filesUpload.length > 0) saveFilesMutate.mutate()
  }, [filesUpload, setFilesUpload])

  const updateGroupId = (id: string) => {
    setGroupId(id)
    localStorage.setItem('current-group-id', id)
  }

  const handleUpload = async (
    file: File
  ): Promise<CreateFileRequestWithURL | void> => {
    if (isFileOverLimit(file)) throw new Error('File is over the limit size')
    const newFile = (await resizeHDImage(file)) ?? file
    const contentType =
      newFile.name
        .substring(newFile.name.lastIndexOf('.') + 1, newFile.name.length)
        .toUpperCase() || newFile.name

    try {
      const { fileName } = await fileUploadInstance.presignAndUploadFile({
        fileName: newFile.name,
        contentType: contentType as FileExtension,
        folderName: 'FILES',
        file: newFile
      })

      const tempFile: CreateFileRequestWithURL = {
        fileName: fileName,
        fileContentType: contentType,
        folderId: folderId ?? '',
        fileSize: newFile.size,
        originalFileName: newFile.name,
        fileUrl: URL.createObjectURL(newFile)
      }

      return tempFile
    } catch {
      return void useToast({
        description: 'Erro ao fazer upload',
        status: 'error'
      })
    }
  }
  const saveFilesMutate = useMutation({
    mutationKey: ['uploadFilesDrive', groupId],
    mutationFn: async () => {
      try {
        const uploadPromises = filesUpload.map((file) => handleUpload(file))
        const uploadedFiles = (await Promise.all(uploadPromises)).filter(
          (file): file is CreateFileRequestWithURL => file !== undefined
        )
        const finalFilesToSend = uploadedFiles.map((file) => ({
          fileName: file.fileName,
          originalFileName: file.originalFileName,
          fileSize: file.fileSize,
          fileContentType: file.fileContentType,
          folderId: folderId ?? ''
        }))

        const [, error] = isGroup
          ? await Api.uploadFilesGroup(groupId ?? '', finalFilesToSend)
          : await Api.uploadFiles(groupId ?? '', finalFilesToSend)

        if (error) {
          return toast({
            title: 'Erro ao salvar arquivos',
            description:
              'Ocorreu um erro ao salvar os arquivos, tente novamente',
            status: 'error'
          })
        }

        toast({
          title: 'Arquivos salvos com sucesso',
          description: 'Os arquivos foram salvos com sucesso',
          status: 'success'
        })
        queryClient.invalidateQueries({
          queryKey: ['fetchFilesAndFolders', groupId]
        })
      } catch {
        toast({
          title: 'Erro ao salvar arquivos',
          description: 'Ocorreu um erro ao salvar os arquivos, tente novamente',
          status: 'error'
        })
      }
    },
    onSuccess() {
      setFilesUpload([])
    }
  })

  return (
    <galleryContext.Provider
      value={{
        groupId,
        setGroupId: updateGroupId,
        groupInfo,
        setGroupInfo,
        membersGroup,
        setMembersGroup,
        filesUpload,
        setFilesUpload,
        isGroup,
        setIsGroup,
        isLoading: saveFilesMutate.isPending
      }}
    >
      {children}
    </galleryContext.Provider>
  )
}
